// Data for the Illustrations page: the ledger (`curriculum_images`), module
// titles (`curriculum_modules`), and the published lesson blocks
// (`module_content`) that carry each illustration's caption and brief.
import { supabase } from '../../lib/supabase';
import { splitImageContent } from './recipe';

export const BUCKET_BASE = `${((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').replace(/\/+$/, '')}/storage/v1/object/public/curriculum-images/`;

export type ImageStatus = 'placed' | 'missing' | 'redo' | 'retired';

export type ImageRow = {
  filename: string;
  module_id: string | null;
  lesson_id: string | null;
  block_id: string | null;
  status: ImageStatus;
  storage_path: string | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  uploaded_at: string | null;
  prompt_used: string | null;
  updated_at: string;
};

export type BlockInfo = {
  lessonTitle: string;
  caption: string;
  brief: string;
  /** Heading nearest above the block, for the drawer. */
  section: string;
};

export type ModuleDoc = {
  title: string;
  lessons: Map<string, string>; // lesson_id → title
  blocks: Map<string, BlockInfo>; // block_id → info
  byFilename: Map<string, BlockInfo>; // filename → first block naming it
};

type RawBlock = { id?: string; type?: string; content?: unknown; brief?: unknown; caption?: unknown };
type RawLesson = { id?: string; title?: string; content?: RawBlock[] };
type RawDoc = { title?: string; lessons?: RawLesson[] };

export const STATUS_LABEL: Record<ImageStatus, string> = {
  placed: 'Placed',
  missing: 'Missing',
  redo: 'Marked for redo',
  retired: 'Retired',
};

export function publicUrl(row: Pick<ImageRow, 'filename' | 'storage_path'>, version?: string | number | null): string {
  const path = row.storage_path || row.filename;
  return BUCKET_BASE + path + (version ? `?v=${encodeURIComponent(String(version))}` : '');
}

export async function loadRows(): Promise<ImageRow[]> {
  const { data, error } = await supabase
    .from('curriculum_images')
    .select('filename, module_id, lesson_id, block_id, status, storage_path, width, height, bytes, uploaded_at, prompt_used, updated_at')
    .order('module_id')
    .order('lesson_id')
    .order('filename');
  if (error) throw error;
  return (data ?? []) as ImageRow[];
}

export async function loadModuleTitles(): Promise<Map<string, { title: string; sort: number }>> {
  const { data, error } = await supabase.from('curriculum_modules').select('module_id, title, sort_order');
  if (error) throw error;
  const m = new Map<string, { title: string; sort: number }>();
  for (const r of (data ?? []) as Array<{ module_id: string; title: string; sort_order: number }>) {
    m.set(r.module_id, { title: r.title, sort: r.sort_order ?? 999 });
  }
  return m;
}

function parseDoc(raw: RawDoc): ModuleDoc {
  const lessons = new Map<string, string>();
  const blocks = new Map<string, BlockInfo>();
  const byFilename = new Map<string, BlockInfo>();
  for (const lesson of raw.lessons ?? []) {
    const lessonId = lesson.id ?? '';
    const lessonTitle = lesson.title ?? lessonId;
    lessons.set(lessonId, lessonTitle);
    let section = '';
    for (const block of lesson.content ?? []) {
      if (block.type === 'heading') section = String(block.content ?? '');
      if (block.type !== 'image_placeholder') continue;
      const content = String(block.content ?? '');
      const { filename, caption } = splitImageContent(content);
      const info: BlockInfo = {
        lessonTitle,
        caption: caption || String(block.caption ?? ''),
        brief: String(block.brief ?? '').trim() || caption,
        section,
      };
      if (block.id) blocks.set(block.id, info);
      if (filename && !byFilename.has(filename)) byFilename.set(filename, info);
    }
  }
  return { title: raw.title ?? '', lessons, blocks, byFilename };
}

const docCache = new Map<string, Promise<ModuleDoc>>();

/** One published module document, parsed once per session. */
export function loadModuleDoc(moduleId: string): Promise<ModuleDoc> {
  let p = docCache.get(moduleId);
  if (!p) {
    p = (async () => {
      const { data, error } = await supabase.from('module_content').select('content').eq('module_id', moduleId).maybeSingle();
      if (error) throw error;
      return parseDoc(((data?.content ?? {}) as RawDoc) || {});
    })();
    docCache.set(moduleId, p);
    p.catch(() => docCache.delete(moduleId));
  }
  return p;
}

/** The block a row points at: by block id first, then by filename. */
export function blockFor(row: ImageRow, doc: ModuleDoc | undefined): BlockInfo | null {
  if (!doc) return null;
  if (row.block_id) {
    const b = doc.blocks.get(row.block_id);
    if (b) return b;
  }
  return doc.byFilename.get(row.filename) ?? null;
}
